import { css } from 'lit';

export const styles = css`
  :host {
    display: block;
    position: relative;
    font-family: 'Google Sans', Roboto, Arial, sans-serif;
    --primary-color: #1a73e8;
    --error-red: #d93025;
    --grammar-purple: #9747ff;
    --surface-1: #fff;
    --surface-2: #f8f9fa;
    --surface-3: #f1f3f4;
    --text-primary: #202124;
    --text-secondary: #5f6368;
    --border-color: #dadce0;
    --shadow-1: 0 1px 2px 0 rgba(60, 64, 67, 0.3),
      0 1px 3px 1px rgba(60, 64, 67, 0.15);
    --shadow-2: 0 1px 3px 0 rgba(60, 64, 67, 0.3),
      0 4px 8px 3px rgba(60, 64, 67, 0.15);
  }
  :host,
  :host * {
    box-sizing: border-box;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid var(--border-color);
    background: var(--surface-2);
    border-radius: 8px 8px 0 0;
  }

  .language-selector {
    padding: 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--surface-1);
    color: var(--text-primary);
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
    transition:
      background 0.2s,
      border-color 0.2s;
  }

  .language-selector:hover {
    background: var(--surface-3);
    border-color: var(--text-secondary);
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-primary);
    font-size: 14px;
  }

  .rewrite-button {
    background: var(--grammar-purple);
    color: white;
  }

  .rewrite-button:hover {
    background: #7a3cd3;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .container {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 16px;
  }

  .container.no-sidebar {
    grid-template-columns: 1fr;
  }
  .content-editable {
    overflow-y: auto;
    padding-right: 48px;
  }

  .content-editable > div {
    outline: none;
    min-height: 300px;
    line-height: 1.6;
    color: var(--text-primary);
    font-size: 16px;
    padding-right: 8px;
    flex: 1;
    overflow-y: auto;
    padding-bottom: 70px;
    -webkit-spell-check: false !important;
    spellcheck: false !important;
  }

  .char-count.over-limit {
    color: var(--error-red);
    font-weight: 500;
    animation: pulse 1s ease-in-out;
  }

  .sidebar {
    border-radius: 4px;
    padding: 16px;
    padding-top: 0;
    max-height: 500px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #121212 var(--surface-2);
  }

  /* Webkit browsers (Chrome, Safari, etc) */
  .sidebar::-webkit-scrollbar {
    width: 8px;
  }

  .sidebar::-webkit-scrollbar-track {
    background: var(--surface-2);
    border-radius: 4px;
  }

  .sidebar::-webkit-scrollbar-thumb {
    background-color: #121212;
    border-radius: 4px;
    border: 2px solid var(--surface-2);
  }

  .sidebar::-webkit-scrollbar-thumb:hover {
    background-color: var(--text-secondary);
  }

  .error-details-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .error-detail {
    background: var(--surface-2);
    border-radius: 4px;
    padding: 10px;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
  }

  .error-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .error-header h3 {
    margin: 0;
    font-size: 16px;
    color: var(--text-primary);
    font-weight: 500;
  }

  .apply-correction-button {
    padding: 6px 12px;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .apply-correction-button:hover {
    background: #1557b0;
  }

  .suggestions-list {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .suggestion-button {
    padding: 6px 12px;
    background: var(--surface-1);
    border: 1px solid var(--border-color);
    border-radius: 16px;
    color: var(--text-primary);
    font-size: 13px;
    cursor: pointer;
    transition:
      background 0.2s,
      border-color 0.2s;
  }

  .suggestion-button:hover {
    background: var(--surface-3);
    border-color: var(--text-secondary);
  }

  .spelling-error {
    text-underline-offset: 3px;
    text-decoration: underline;
    text-decoration-color: var(--error-red);
    text-decoration-thickness: 2px;
    cursor: pointer;
    transition: text-decoration-thickness 0.2s ease;
  }

  .grammar-error {
    text-underline-offset: 3px;
    text-decoration: underline;
    text-decoration-color: var(--grammar-purple);
    text-decoration-thickness: 2px;
    cursor: pointer;
    transition: text-decoration-thickness 0.2s ease;
  }

  .source-ai {
    /* text-decoration-style: double; */
    text-decoration-style: underline;
  }

  p {
    margin: 8px 0;
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.5;
  }

  strong {
    color: var(--text-primary);
  }

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .container {
      grid-template-columns: 1fr;
    }

    .controls {
      flex-direction: column;
      align-items: stretch;
    }

    .check-button {
      margin-left: 0;
    }
  }

  .hover-card {
    position: absolute;
    background: var(--surface-1);
    border-radius: 4px;
    padding: 10px;
    box-shadow: var(--shadow-2);
    min-width: 200px;
    max-width: 300px;
    z-index: 1000;
    animation: fadeIn 0.2s ease-in-out;
    pointer-events: auto;
  }

  .hover-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .hover-card-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    margin: 0;
  }

  .hover-card-content {
    font-size: 13px;
    color: var(--text-secondary);
    margin-bottom: 12px;
  }

  .hover-card-suggestion {
    color: var(--primary-color);
    font-weight: 500;
    font-size: 14px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .error-counts {
    display: flex;
    gap: 24px;
    border-bottom: 1px solid var(--border-color);
  }

  .error-count {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .error-count .count {
    font-size: 20px;
    font-weight: 500;
    padding: 4px 12px;
    border-radius: 16px;
    color: white;
  }

  .error-count.spelling .count {
    background: var(--error-red);
  }

  .error-count.grammar .count {
    background: var(--grammar-purple);
  }

  .error-count .label {
    font-size: 14px;
    color: var(--text-secondary);
  }

  .error-word {
    font-weight: 500;
    color: var(--primary-color);
    margin-bottom: 8px;
  }

  .copy-icon-button {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 4px;
    background: var(--surface-1);
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    opacity: 0.7;
  }

  .copy-icon-button:hover {
    background: var(--surface-3);
    color: var(--text-primary);
    opacity: 1;
  }

  .copy-icon-button svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  .copy-feedback {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--text-primary);
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 14px;
    box-shadow: var(--shadow-2);
    z-index: 1000;
    animation: slideUp 0.2s ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translate(-50%, 20px);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }

  .paste-area {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    border: 2px dashed var(--border-color);
    border-radius: 8px;
    background: var(--surface-2);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    width: 80%;
    max-width: 300px;
  }

  .paste-area:hover {
    background: var(--surface-3);
    border-color: var(--primary-color);
    color: var(--text-primary);
  }

  .paste-area svg {
    width: 32px;
    height: 32px;
    fill: currentColor;
  }

  .paste-text {
    font-size: 16px;
    font-weight: 500;
  }

  .paste-shortcut {
    font-size: 12px;
    padding: 4px 8px;
    background: var(--surface-1);
    border-radius: 4px;
    color: var(--text-secondary);
  }

  .content-editable > div:empty {
    min-height: 300px;
  }

  .editor-actions {
    position: absolute;
    top: 8px;
    right: 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 8px;
    border: none;
    border-radius: 50%;
    background: var(--surface-1);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .icon-button:hover {
    background: var(--surface-3);
    color: var(--text-primary);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  }

  .icon-button svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  .icon-text-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    background: var(--surface-1);
    color: var(--text-primary);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.25px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .icon-text-button:hover {
    background: var(--surface-3);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  .apply-correction-button {
    background: var(--primary-color);
    color: var(--surface-1);
  }
  .apply-correction-button:hover {
    background: #1557b0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  }
  .suggestion-button:hover {
    background: var(--surface-3);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  .diff-container {
    padding: 16px;
    background: var(--surface-2);
    border-bottom: 1px solid var(--border-color);
  }

  .diff-content {
    font-size: 14px;
    line-height: 1.6;
    padding: 12px;
    background: var(--surface-1);
    border-radius: 4px;
  }

  .diff-removed {
    background: #ffd7d7;
    text-decoration: line-through;
    color: #b71c1c;
    padding: 0 2px;
  }

  .diff-added {
    background: #d7ffd7;
    color: #1b5e20;
    padding: 0 2px;
    margin-left: 4px;
  }

  .diff-controls {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .icon-text-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--surface-1);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .icon-text-button:hover {
    background: var(--surface-3);
    border-color: var(--text-secondary);
  }

  .icon-text-button svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }

  .corrected-text {
    line-height: 1.6;
    color: var(--text-primary);
    white-space: pre-wrap;
  }

  .no-errors-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;
    color: #0f9d58;
    background: #e6f4ea;
    border-radius: 4px;
  }

  .no-errors-container svg {
    width: 48px;
    height: 48px;
    fill: currentColor;
    margin-bottom: 16px;
  }

  .no-errors-container span {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .no-errors-container p {
    font-size: 14px;
    color: #121212;
    margin: 0;
  }

  .char-count.over-limit {
    color: var(--error-red);
    font-weight: 500;
    animation: pulse 1s ease-in-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  .char-count.over-limit {
    color: var(--error-red);
    font-weight: 500;
    animation: pulse 1s ease-in-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  .char-count.warning-pulse {
    animation: warning-pulse 0.5s ease-in-out;
  }

  @keyframes warning-pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  .editor-wrapper.warning-pulse {
    animation: border-pulse 0.5s ease-in-out;
  }

  @keyframes border-pulse {
    0% {
      border-color: var(--border-color);
    }
    50% {
      border-color: var(--error-red);
      border-width: 2px;
      box-shadow: 0 0 8px rgba(255, 0, 0, 0.2);
    }
    100% {
      border-color: var(--border-color);
    }
  }

  .editor-section {
    position: relative;
    flex: 1;
    height: max-content;
  }

  .editor-wrapper {
    position: relative;
    min-height: 300px;
    padding: 16px;
    padding-right: 0;
    background: var(--surface-1);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    max-height: 500px;
    display: flex;
    flex-direction: column;
  }

  .usage-count,
  .char-count {
    font-size: 12px;
    color: var(--text-secondary);
    background: var(--surface-1);
    border-radius: 4px;
    transition: all 0.2s ease;
    z-index: 1;
  }

  .editor-controls {
    display: flex;
    gap: 16px;
    margin-top: 16px;
    align-items: center;
  }
  .editor-controls sg-feedback {
    margin-left: auto;
  }
  @media (max-width: 768px) {
    .check-button-onscreen {
      position: fixed;
      bottom: 10px;
      left: 5px;
      z-index: 9999999;
      width: calc(50% - 10px);
      height: 40px;
      padding: 0 10px;
    }
    .check-button-onscreen.check-button-has-errors {
      width: calc(100% - 10px);
    }

    .apply-all-button-onscreen {
      position: fixed;
      bottom: 10px;
      right: 5px;
      z-index: 2;
      width: calc(50% - 10px);
      height: 40px;
    }
  }
  .check-button {
    background: #121212;
    color: white;
  }

  .check-button:hover {
    background: #1557b0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .check-button:disabled {
    background: var(--text-secondary);
    cursor: not-allowed;
  }

  .check-button,
  .apply-all-button,
  .rewrite-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    padding: 0 24px;
    border: none;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.25px;
    transition: background 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    cursor: pointer;
  }
  .apply-all-button {
    background: var(--primary-color);
    color: white;
  }

  .apply-all-button:hover {
    background: #1557b0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  }

  .counters {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: var(--surface-1);
    border-top: 1px solid var(--border-color);
    z-index: 2;
  }

  @media (max-width: 480px) {
    .counters {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      padding: 8px;
    }

    .error-counts {
      width: 100%;
    }

    .error-count {
      font-size: 12px;
    }

    .error-count .count {
      padding: 2px 6px;
      font-size: 12px;
    }

    .error-count .label {
      font-size: 11px;
    }

    .char-count,
    .usage-count {
      font-size: 11px;
    }
  }

  .error-counts {
    display: flex;
    gap: 12px;
    background: var(--surface-1);
    border: 0;
  }

  .error-count {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .error-count .count {
    font-size: 12px;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 10px;
    color: white;
  }

  .error-count .label {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .error-message {
    background-color: #ff4444;
    color: white;
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 16px;
    text-align: center;
    animation: fadeIn 0.3s ease-in-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .usage-count.over-limit {
    color: var(--error-red);
    font-weight: 500;
  }

  .loading-spinner {
    animation: rotate 2s linear infinite;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  .no-errors-container.loading {
    background: var(--surface-2);
    color: #121212;
  }

  .no-errors-container.loading svg {
    fill: #121212;
  }

  .error-icon {
    width: 20px;
    height: 20px;
    fill: currentColor;
    vertical-align: middle;
    margin-right: 8px;
  }

  .error-header h3 {
    display: flex;
    align-items: center;
    margin: 0;
    font-size: 16px;
    color: var(--text-primary);
    font-weight: 500;
  }

  .hover-card-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    margin: 0;
  }

  @media (max-width: 768px) {
    .hover-card {
      position: absolute;
      left: 50% !important;
      transform: translateX(-50%);
      width: calc(100% - 32px);
      max-width: 300px;
      margin: 0 auto;
    }

    .editor-wrapper {
      position: relative;
    }
  }

  .success-message {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #0f9d58;
    font-size: 14px;
    animation: fadeIn 0.3s ease-out;
  }

  .success-message svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }

  @media (max-width: 768px) {
    .success-message {
      display: none;
    }
  }

  .usage-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .success-message {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #0f9d58;
    font-size: 12px;
    animation: fadeIn 0.3s ease-out;
  }

  .success-message svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;
